/**
 * Minimal JWT payload decoding for reading identity claims from the existing
 * access token (AB#8667). No signature verification is performed — the token
 * is already trusted (it is what authenticates every API call); this only
 * reads display claims for the profile menu. No new API is introduced.
 */

export interface JwtPayload {
    [claim: string]: unknown;
}

export interface UserIdentity {
    /** Display name, or null when no name-like claim is present. */
    name: string | null;
    /** Role, or null when no role-like claim is present. */
    role: string | null;
    /** Tenant, or null when no tenant-like claim is present. */
    tenant: string | null;
}

/**
 * Common claim key aliases per identity field, checked in priority order.
 * Kept broad because role/tenant claim names vary by identity provider; the
 * first non-empty match wins and anything unknown renders as an em-dash
 * rather than a fabricated value (AB#8654 lesson).
 */
const NAME_CLAIMS = [
    'name',
    'preferred_username',
    'unique_name',
    'given_name',
    'nickname',
    'email',
    'sub',
];
const ROLE_CLAIMS = [
    'role',
    'roles',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
];
const TENANT_CLAIMS = [
    'tenant',
    'tenant_name',
    'tenantId',
    'tenant_id',
    'tid',
    'organization',
    'org',
];

function base64UrlDecode(segment: string): string | null {
    try {
        const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(
            base64.length + ((4 - (base64.length % 4)) % 4),
            '=',
        );
        const binary = atob(padded);
        // Handle UTF-8 encoded characters in claim values.
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    } catch {
        return null;
    }
}

/**
 * Decode the payload of a JWT without verifying its signature.
 * Returns null for malformed tokens.
 */
export function decodeJwtPayload(token: string | null): JwtPayload | null {
    if (!token) {
        return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
        return null;
    }

    const json = base64UrlDecode(parts[1]);
    if (json === null) {
        return null;
    }

    try {
        const parsed = JSON.parse(json);
        return parsed && typeof parsed === 'object'
            ? (parsed as JwtPayload)
            : null;
    } catch {
        return null;
    }
}

function firstClaim(
    payload: JwtPayload,
    keys: readonly string[],
): string | null {
    for (const key of keys) {
        const value = payload[key];
        if (typeof value === 'string' && value.trim() !== '') {
            return value.trim();
        }
        if (Array.isArray(value)) {
            const firstString = value.find(
                (entry): entry is string =>
                    typeof entry === 'string' && entry.trim() !== '',
            );
            if (firstString) {
                return firstString.trim();
            }
        }
    }
    return null;
}

/**
 * Extract name/role/tenant from a decoded JWT payload. Missing claims are
 * returned as null so the UI can render an explicit unknown state.
 */
export function extractUserIdentity(
    payload: JwtPayload | null,
): UserIdentity {
    if (!payload) {
        return { name: null, role: null, tenant: null };
    }

    return {
        name: firstClaim(payload, NAME_CLAIMS),
        role: firstClaim(payload, ROLE_CLAIMS),
        tenant: firstClaim(payload, TENANT_CLAIMS),
    };
}
